import { utcToZonedCalendarDate } from './conversion';
import { formatLocalDay } from './day';
import { normalizeTimeZone } from './normalize';

// why: ordered to match Date.getUTCDay(), so a weekday reads as WEEKDAY_KEYS[d.getUTCDay()] with no
// why: offset arithmetic anywhere.
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export type WorkingDayMask = Record<WeekdayKey, boolean>;

export type WorkingDayCalendar = {
  timeZone: string;
  workingDays: WorkingDayMask;
  holidays: readonly string[];
};

const DAY_MS = 86_400_000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value: number): string => String(value).padStart(2, '0');

const dayToUtcMs = (day: string): number => {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number);

  return Date.UTC(year, month - 1, date);
};

const utcMsToDay = (ms: number): string => {
  const date = new Date(ms);

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

const weekdayOf = (utcMs: number): WeekdayKey => {
  // why: getUTCDay is 0-6 and WEEKDAY_KEYS is ordered to match, so the fallback is unreachable —
  // why: it exists because an indexed read is typed as possibly undefined.
  const [sunday] = WEEKDAY_KEYS;

  return WEEKDAY_KEYS[new Date(utcMs).getUTCDay()] ?? sunday;
};

// gloss
export const toCalendarDay = (value: Date | string | null | undefined, timeZone: string): string | null => {
  if (!value) return null;

  // why: new Date('2025-09-12') is UTC midnight, which projects into Pacific as the 11th — a value
  // why: that is already a calendar day has no instant to reinterpret.
  if (typeof value === 'string' && DATE_ONLY.test(value)) return value;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return formatLocalDay(utcToZonedCalendarDate(date, normalizeTimeZone(timeZone)));
};

const workingDaysPerWeek = (calendar: WorkingDayCalendar): number =>
  WEEKDAY_KEYS.filter((key) => calendar.workingDays[key]).length;

export const isWorkingDay = (day: string, calendar: WorkingDayCalendar): boolean =>
  !!calendar.workingDays[weekdayOf(dayToUtcMs(day))] && !calendar.holidays.includes(day);

// why: all stepping happens on UTC midnights — a +24h step in a zone with DST lands on the same
// why: calendar day twice a year.
const countWorkingDays = (from: string, to: string, calendar: WorkingDayCalendar): number => {
  const workingPerWeek = workingDaysPerWeek(calendar);
  if (workingPerWeek === 0) return 0;

  const fromMs = dayToUtcMs(from);
  const days = (dayToUtcMs(to) - fromMs) / DAY_MS;
  const wholeWeeks = Math.floor(days / 7);

  let total = wholeWeeks * workingPerWeek;

  for (let offset = wholeWeeks * 7 + 1; offset <= days; offset += 1) {
    if (calendar.workingDays[weekdayOf(fromMs + offset * DAY_MS)]) total += 1;
  }

  for (const holiday of new Set(calendar.holidays)) {
    if (holiday <= from || holiday > to) continue;
    if (calendar.workingDays[weekdayOf(dayToUtcMs(holiday))]) total -= 1;
  }

  return total;
};

// gloss
export const businessDaysBetween = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined,
  calendar: WorkingDayCalendar,
): number | null => {
  const startDay = toCalendarDay(start, calendar.timeZone);
  const endDay = toCalendarDay(end, calendar.timeZone);
  if (!startDay || !endDay) return null;

  if (startDay === endDay) return 0;

  // why: a negative result keeps a completedAt-before-requestedAt row visible instead of clamping it
  // why: to zero and reading as same-day delivery.
  return startDay < endDay
    ? countWorkingDays(startDay, endDay, calendar)
    : -countWorkingDays(endDay, startDay, calendar);
};

// gloss
export const addBusinessDays = (
  start: Date | string | null | undefined,
  days: number,
  calendar: WorkingDayCalendar,
): string | null => {
  const startDay = toCalendarDay(start, calendar.timeZone);
  if (!startDay) return null;
  if (days <= 0) return startDay;

  const holidays = new Set(calendar.holidays);
  const perWeek = workingDaysPerWeek(calendar);
  if (perWeek === 0) return null;

  // why: on a one-working-day calendar each holiday costs a whole week, so the bound counts weeks of
  // why: work rather than days.
  const limit = (Math.ceil((days + holidays.size) / perWeek) + 1) * 7;

  let ms = dayToUtcMs(startDay);
  let counted = 0;

  for (let step = 0; step < limit && counted < days; step += 1) {
    ms += DAY_MS;
    if (calendar.workingDays[weekdayOf(ms)] && !holidays.has(utcMsToDay(ms))) counted += 1;
  }

  return counted === days ? utcMsToDay(ms) : null;
};
